import { get, getRuntimeConfig } from '@/utils/runtime-config';

// Helper per ottenere la config in modo sicuro
function getConfig() {
  try {
    return getRuntimeConfig();
  } catch (e) {
    // Fallback durante il caricamento iniziale
    return null;
  }
}

// Default logo for tokens without a LOGO field
const DEFAULT_TOKEN_LOGO =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHBhdGggZmlsbD0iI0ZGQjgwMCIgZD0iTTEyIDJDNi40OCAyIDIgNi40OCAyIDEyczQuNDggMTAgMTAgMTAgMTAtNC40OCAxMC0xMFMxNy41MiAyIDEyIDJ6bTAgMThjLTQuNDEgMC04LTMuNTktOC04czMuNTktOCA4LTggOCAzLjU5IDggOC0zLjU5IDgtOCA4eiIvPjxwYXRoIGZpbGw9IiNGRkI4MDAiIGQ9Ik0xMi41IDdoLTF2Mkg5djJoMi41djJoLTJ2Mmgydi41aDJ2LS41SDEzYzEuMSAwIDItLjkgMi0ycy0uOS0yLTItMnYtMmMyIDAgMi0uOSAyLTJzLS45LTItMi0yaC0uNVY3eiIvPjwvc3ZnPg==';

// Parse token from runtime config and apply defaults
const parseTokenWithDefaults = (token) => {
  if (!token.LOGO || token.LOGO.trim() === '') {
    token.LOGO = DEFAULT_TOKEN_LOGO;
  }
  return token;
};

// Storage Keys (dinamici dal runtime config)
const BROWSER_STORAGE_KEYS = Object.freeze({
  get LOCALE() {
    const prefix = get('branding.storagePrefix', 'commercio-explorer');
    return `${prefix}-locale`;
  },
  get THEME() {
    const prefix = get('branding.storagePrefix', 'commercio-explorer');
    return `${prefix}-dark-theme`;
  },
  get VALIDATORS() {
    const prefix = get('branding.storagePrefix', 'commercio-explorer');
    return `${prefix}-my-validators`;
  },
});

// Static constants
const LEGACY_VERSIONS = Object.freeze({
  V_038: '0.38',
});

const SCROLL = Object.freeze({
  BEHAVIOR: 'smooth',
  OFFSET_X: 0,
  OFFSET_Y: 70,
});

const REST_LEAD_TIME = 5 * 1000;

// Create CONFIG object with dynamic getters
const CONFIG = {};

Object.defineProperty(CONFIG, 'BROWSER_STORAGE_KEYS', {
  get() {
    return BROWSER_STORAGE_KEYS;
  },
  enumerable: true,
});

Object.defineProperty(CONFIG, 'COPYRIGHT', {
  get() {
    const config = getConfig();
    return (
      config?.branding?.copyright ||
      'Powered by <a href="https://commercio.network" target="_blank" rel="noopener noreferrer">Commercio.network SpA</a>'
    );
  },
  enumerable: true,
});

Object.defineProperty(CONFIG, 'ANCESTORS', {
  get() {
    const config = getConfig();
    const ancestorsString = config?.legacy?.ancestors || '[]';
    try {
      return JSON.parse(ancestorsString);
    } catch {
      return [];
    }
  },
  enumerable: true,
  configurable: true,
});

Object.defineProperty(CONFIG, 'FIRST_HEIGHT', {
  get() {
    const config = getConfig();
    return config?.chain?.firstHeight?.toString() || '1';
  },
  enumerable: true,
});

Object.defineProperty(CONFIG, 'HRP', {
  get() {
    const config = getConfig();
    return config?.chain?.hrp || 'did:com:';
  },
  enumerable: true,
});

Object.defineProperty(CONFIG, 'PREFIXES', {
  get() {
    const hrp = CONFIG.HRP;
    return Object.freeze({
      ACCOUNT: {
        get ADDRESS() {
          return hrp;
        },
        get KEY() {
          return `${hrp}pub`;
        },
      },
      VALIDATOR: {
        CONSENSUS: {
          get ADDRESS() {
            return `${hrp}valcons`;
          },
          get KEY() {
            return `${hrp}valconspub`;
          },
        },
        OPERATOR: {
          get ADDRESS() {
            return `${hrp}valoper`;
          },
          get KEY() {
            return `${hrp}valoperpub`;
          },
        },
      },
    });
  },
  enumerable: true,
});

Object.defineProperty(CONFIG, 'TOKEN_CREDIT', {
  get() {
    const config = getConfig();
    const token = config?.tokens?.credit || {
      name: 'Commercio Cash Credit',
      symbol: 'CCC',
      denom: 'uccc',
      exponent: 6,
      logo: '',
    };
    // Convert to uppercase property names for backward compatibility
    const normalizedToken = {
      NAME: token.name,
      SYMBOL: token.symbol,
      DENOM: token.denom,
      EXPONENT: token.exponent,
      LOGO: token.logo,
    };
    return Object.freeze(parseTokenWithDefaults(normalizedToken));
  },
  enumerable: true,
});

Object.defineProperty(CONFIG, 'TOKEN_STAKE', {
  get() {
    const config = getConfig();
    const token = config?.tokens?.stake || {
      name: 'Commercio',
      symbol: 'COM',
      denom: 'ucommercio',
      exponent: 6,
      logo: '',
    };
    // Convert to uppercase property names for backward compatibility
    const normalizedToken = {
      NAME: token.name,
      SYMBOL: token.symbol,
      DENOM: token.denom,
      EXPONENT: token.exponent,
      LOGO: token.logo,
    };
    return Object.freeze(parseTokenWithDefaults(normalizedToken));
  },
  enumerable: true,
});

Object.defineProperty(CONFIG, 'TOKEN_REWARDS', {
  get() {
    const config = getConfig();
    const tokens = config?.tokens || {};

    // Cerca il token con isRewardable: true
    const rewardableToken = Object.values(tokens).find(
      (token) => token.isRewardable === true
    );

    // Fallback al token stake se nessuno è marcato come rewardable (retrocompatibilità)
    const token = rewardableToken || tokens.stake || {
      name: 'Commercio',
      symbol: 'COM',
      denom: 'ucommercio',
      exponent: 6,
      logo: '',
    };

    // Convert to uppercase property names for backward compatibility
    const normalizedToken = {
      NAME: token.name,
      SYMBOL: token.symbol,
      DENOM: token.denom,
      EXPONENT: token.exponent,
      LOGO: token.logo,
    };
    return Object.freeze(parseTokenWithDefaults(normalizedToken));
  },
  enumerable: true,
});

Object.defineProperty(CONFIG, 'COIN_TYPE', {
  get() {
    const config = getConfig();
    return config?.chain?.coinType || 118;
  },
  enumerable: true,
});

Object.defineProperty(CONFIG, 'GAS_PRICE_STEP', {
  get() {
    const config = getConfig();
    const gas = config?.gas?.priceStep;
    return Object.freeze({
      AVERAGE: gas?.average || 0.125,
      HIGH: gas?.high || 0.125,
      LOW: gas?.low || 0.125,
    });
  },
  enumerable: true,
});

Object.defineProperty(CONFIG, 'FEE_AMOUNT', {
  get() {
    const config = getConfig();
    return config?.gas?.feeAmount || 10000;
  },
  enumerable: true,
});

Object.defineProperty(CONFIG, 'GAS_AMOUNT', {
  get() {
    const config = getConfig();
    return config?.gas?.gasAmount || 1000000;
  },
  enumerable: true,
});

Object.defineProperty(CONFIG, 'WASM_CW20_CODE_ID', {
  get() {
    const config = getConfig();
    return config?.cosmwasm?.cw20CodeId || '';
  },
  enumerable: true,
  configurable: true,
});

Object.defineProperty(CONFIG, 'WASM_SWAP_CODE_ID', {
  get() {
    const config = getConfig();
    return config?.cosmwasm?.swapCodeId || '';
  },
  enumerable: true,
  configurable: true,
});

// Static properties
CONFIG.LEGACY_VERSIONS = LEGACY_VERSIONS;
CONFIG.REST_LEAD_TIME = REST_LEAD_TIME;
CONFIG.SCROLL = SCROLL;

export default CONFIG;
